/**
 * Utilities for voice agent version compare (delta vs previous_values).
 */

function stableStringify(obj: unknown): string {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
  const keys = Object.keys(obj as Record<string, unknown>).sort((a, b) => a.localeCompare(b));
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify((obj as Record<string, unknown>)[k])}`)
    .join(',')}}`;
}

function deepEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

export function applyDelta(
  previous: Record<string, unknown>,
  delta: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...previous };
  for (const [k, v] of Object.entries(delta)) {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      (out as Record<string, unknown>)[k] = applyDelta(
        (((out as Record<string, unknown>)[k] as Record<string, unknown>) || {}) as Record<
          string,
          unknown
        >,
        v as Record<string, unknown>
      );
    } else {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

export function getChangedPaths(delta: Record<string, unknown>, prefix = ''): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(delta)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof (v as { getMonth?: unknown }).getMonth !== 'function'
    ) {
      out.push(...getChangedPaths(v as Record<string, unknown>, path));
    } else {
      out.push(path);
    }
  }
  return out;
}

export function getAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  return deepEqual(a, b);
}

export interface VersionCompareRows {
  beforeRows: Array<{ key: string; value: unknown }>;
  afterRows: Array<{ key: string; value: unknown }>;
}

/** Derive before/after rows for a version detail payload. */
export function computeVersionCompareRows(detail: {
  delta: Record<string, unknown>;
  previous_values: Record<string, unknown>;
}): VersionCompareRows {
  const prevValues =
    detail.previous_values && typeof detail.previous_values === 'object' && !Array.isArray(detail.previous_values)
      ? (detail.previous_values as Record<string, unknown>)
      : {};
  const afterObj = applyDelta(prevValues, detail.delta);
  const candidatePaths = [...getChangedPaths(detail.delta)].sort((a, b) => a.localeCompare(b));
  const changedPaths = candidatePaths.filter(
    (path) => !valuesEqual(getAtPath(prevValues, path), getAtPath(afterObj, path)),
  );
  return {
    beforeRows: changedPaths.map((path) => ({ key: path, value: getAtPath(prevValues, path) })),
    afterRows: changedPaths.map((path) => ({ key: path, value: getAtPath(afterObj, path) })),
  };
}
